/**
 * ThirdPartyScripts — dynamically injects analytics / marketing / functional
 * trackers ONLY when:
 *   1. The user has granted the matching consent category, AND
 *   2. The admin has NOT killed that category (`cookie_<cat>_enabled !== false`).
 *
 * When consent is revoked (or admin toggles the category off), previously
 * injected scripts are removed from the DOM so they stop firing on subsequent
 * page changes. GA/GTM/FB Pixel each set global functions/queues that persist
 * for the current tab session — a full reload is the only way to fully purge
 * those; we surface that expectation implicitly (revoke → reload).
 *
 * Extending: to add a new tracker, add a settings field like `cookie_hotjar_id`
 * to `DEFAULT_SETTINGS` (backend seed.py), a field to `SettingsAdmin` UI, and
 * a new effect block here gated on the appropriate category.
 */
import { useEffect } from "react";
import { useCookieConsent } from "@/contexts/CookieConsentContext";

/** Load an external script exactly once (idempotent by `id`). */
function loadScript({ id, src, inline }) {
  if (!id || typeof document === "undefined") return;
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  if (src) s.src = src;
  if (inline) s.text = inline;
  document.head.appendChild(s);
}

/** Remove a script node by id (best-effort — globals it created remain until reload). */
function removeScript(id) {
  if (typeof document === "undefined") return;
  const el = document.getElementById(id);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/** Inject an admin-supplied raw JS snippet (already trusted — admin-only input). */
function injectCustomSnippet(id, snippet) {
  const code = (snippet || "").trim();
  if (!code) return;
  loadScript({ id, inline: code });
}

export default function ThirdPartyScripts() {
  const { has, settings } = useCookieConsent();

  const analyticsAllowed = has("analytics") && settings.cookie_analytics_enabled !== false;
  const marketingAllowed = has("marketing") && settings.cookie_marketing_enabled !== false;
  const functionalAllowed = has("functional") && settings.cookie_functional_enabled !== false;

  // ---------------- Analytics: GA4 + GTM + custom ----------------
  useEffect(() => {
    if (!analyticsAllowed) {
      removeScript("abg-ga4-loader");
      removeScript("abg-ga4-init");
      removeScript("abg-gtm-loader");
      removeScript("abg-custom-analytics");
      return;
    }
    const ga = (settings.cookie_google_analytics_id || "").trim();
    const gtm = (settings.cookie_google_tag_manager_id || "").trim();
    if (ga) {
      loadScript({ id: "abg-ga4-loader", src: `https://www.googletagmanager.com/gtag/js?id=${ga}` });
      loadScript({
        id: "abg-ga4-init",
        inline:
          `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}` +
          `gtag('js',new Date());gtag('config','${ga}',{anonymize_ip:true});`,
      });
    }
    if (gtm) {
      loadScript({
        id: "abg-gtm-loader",
        inline:
          `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});` +
          `var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';` +
          `j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);` +
          `})(window,document,'script','dataLayer','${gtm}');`,
      });
    }
    injectCustomSnippet("abg-custom-analytics", settings.cookie_custom_analytics_script);
  }, [
    analyticsAllowed,
    settings.cookie_google_analytics_id,
    settings.cookie_google_tag_manager_id,
    settings.cookie_custom_analytics_script,
  ]);

  // ---------------- Marketing: Facebook Pixel + LinkedIn Insight + custom ----------------
  useEffect(() => {
    if (!marketingAllowed) {
      removeScript("abg-fbpixel");
      removeScript("abg-linkedin-init");
      removeScript("abg-linkedin-loader");
      removeScript("abg-custom-marketing");
      return;
    }
    const fbp = (settings.cookie_facebook_pixel_id || "").trim();
    const li = (settings.cookie_linkedin_partner_id || "").trim();
    if (fbp) {
      loadScript({
        id: "abg-fbpixel",
        inline:
          `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?` +
          `n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;` +
          `n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;` +
          `t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,` +
          `document,'script','https://connect.facebook.net/en_US/fbevents.js');` +
          `fbq('init','${fbp}');fbq('track','PageView');`,
      });
    }
    if (li) {
      loadScript({
        id: "abg-linkedin-init",
        inline:
          `_linkedin_partner_id='${li}';window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];` +
          `window._linkedin_data_partner_ids.push(_linkedin_partner_id);`,
      });
      loadScript({ id: "abg-linkedin-loader", src: "https://snap.licdn.com/li.lms-analytics/insight.min.js" });
    }
    injectCustomSnippet("abg-custom-marketing", settings.cookie_custom_marketing_script);
  }, [
    marketingAllowed,
    settings.cookie_facebook_pixel_id,
    settings.cookie_linkedin_partner_id,
    settings.cookie_custom_marketing_script,
  ]);

  // ---------------- Functional: Microsoft Clarity + custom ----------------
  useEffect(() => {
    if (!functionalAllowed) {
      removeScript("abg-clarity");
      removeScript("abg-custom-functional");
      return;
    }
    const clarity = (settings.cookie_microsoft_clarity_id || "").trim();
    if (clarity) {
      loadScript({
        id: "abg-clarity",
        inline:
          `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};` +
          `t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;` +
          `y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);` +
          `})(window,document,"clarity","script","${clarity}");`,
      });
    }
    injectCustomSnippet("abg-custom-functional", settings.cookie_custom_functional_script);
  }, [
    functionalAllowed,
    settings.cookie_microsoft_clarity_id,
    settings.cookie_custom_functional_script,
  ]);

  return null; // effect-only, no DOM
}